/**
 * Deployer Upsell Pack
 * Custom element for handling pack upgrade offers in the cart drawer.
 * Stores upgrade state in sessionStorage to show the offer only once per session.
 */
class DeployerUpsellPack extends HTMLElement {
    constructor() {
        super();
        this.variantId = this.dataset.variantId;
        this.productId = this.dataset.productId;
        this.lineIndex = this.dataset.lineIndex;
        this.currentQty = parseInt(this.dataset.currentQty);
        this.storageKey = `upsell_upgraded_${this.productId}`;
    }

    connectedCallback() {
        // Check if already upgraded in this session
        if (sessionStorage.getItem(this.storageKey) === 'true') {
            this.remove();
            return;
        }

        this.querySelectorAll('.deployer-upsell-pack__upgrade-btn').forEach(btn => {
            btn.addEventListener('click', this.handleUpgrade.bind(this));
        });
    }

    async handleUpgrade(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const targetQty = parseInt(button.dataset.targetQty);

        if (!targetQty || targetQty <= this.currentQty) return;

        // Show loading state
        button.classList.add('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        button.disabled = true;

        try {
            // Step 1: Get cart to find all line items for this product
            const cartResponse = await fetch('/cart.js');
            if (!cartResponse.ok) throw new Error('Failed to fetch cart');
            const cart = await cartResponse.json();

            // Find all line items with this product (may have multiple variants/lines)
            const productLines = cart.items.filter(item => item.product_id.toString() === this.productId);

            // Calculate current total quantity for this product
            const currentTotalQty = productLines.reduce((sum, item) => sum + item.quantity, 0);

            // Calculate quantity to add
            const qtyToAdd = targetQty - currentTotalQty;
            if (qtyToAdd <= 0) return;

            // Step 2: Add the difference to the first variant found (keeps existing selections)
            const firstVariantId = productLines.length > 0 ? productLines[0].variant_id : parseInt(this.variantId);

            const addResponse = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: [{ id: firstVariantId, quantity: qtyToAdd }]
                })
            });

            if (!addResponse.ok) {
                throw new Error('Failed to add items to cart');
            }

            // Mark as upgraded in sessionStorage
            sessionStorage.setItem(this.storageKey, 'true');

            // Refresh cart drawer
            document.dispatchEvent(new CustomEvent('deployer-cart-drawer:refresh'));

        } catch (error) {
            console.error('Error upgrading pack:', error);
            document.dispatchEvent(new CustomEvent('toast:open', { 
                detail: { type: 'error', message: 'Unable to upgrade. Please try again.' } 
            }));
        } finally {
            button.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
            button.disabled = false;
        }
    }
}

if (!customElements.get('deployer-upsell-pack')) {
    customElements.define('deployer-upsell-pack', DeployerUpsellPack);
}
